// src/components/League/LeagueHome.jsx
//
// The redesigned League surface — state machine for the spectate-and-enter front
// end (lobby ⇄ spectate, with the pod sheet / action layer / join confirm as
// overlays). Ported from the Claude Design prototype's LeagueApp, minus the
// design-harness chrome (the iOS device frame, the scale-to-fit, and the Tweaks
// panel are gone). Renders as a centered mobile-width column; the document
// scrolls. The accent is fixed to the league energy teal (the prototype's
// tweakable accent was harness-only).
//
// FIXTURES-FIRST: data comes from the single useLeagueState() seam. The
// "Open my game" affordance (in the lobby) calls onOpenMyGame — LeagueScreen
// handles that as a full-screen push to the real participant flow.

import React from 'react';
import './league.css';
import useLeagueState from '../../hooks/useLeagueState';
import { logLeagueSignal } from '../../services/leagueSignals';
import { useUser } from '../../contexts/UserContext';
import { subscribeMyGroup, subscribeMyMostRecentVoidedGroup, subscribeMyTrainingPod } from '../../services/tournamentGroupService';
import { LEAGUE_NEXT_ARC_ENABLED } from '../../config/featureFlags';
import { LTOKENS, LX } from './leagueTokens';
import Lobby, { LobbyTabbed } from './LeagueLobbyRedesign';
import Spectate from './LeagueSpectate';
import { PodSheet } from './LeaguePod';
import LeagueVoidedNotice from './LeagueVoidedNotice';
// Backing Beta PR 4 — the landing strip (the door) and the screen it opens.
// BackingLandingStrip reads BACKING_BETA_ENABLED at call time and renders
// NOTHING while dark, so the slot below emits no element and opens no read;
// the screen is reachable only through the strip. The strip sits directly
// under the ranked-entry position — the slot picker and the Auto-draft card,
// or the seated hero — as on desktop (N3, the desktop review record).
import BackingLandingStrip from './backing/BackingLandingStrip';
import BackingScreen from './backing/BackingScreen';
import { fetchTapePod } from '../../services/backingService';

const ACCENT = LX.energy; // teal — the league energy accent

// dev hooks for smoke testing (inert in normal use):
//   ?f=forming|filling|open                              → fill level
//   ?s=pod|spectate-live|spectate-final                  → land on a state
const SP = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
const DEV_FILL = ['forming', 'filling', 'open'].includes(SP.get('f')) ? SP.get('f') : 'open';
const DEV_S = SP.get('s') || '';

// League Next-Arc Phase 2 — the persistent Training | Ranked tabs. Gate evaluated
// ONCE (module constant, stable across renders) = the sub-flag OR the dev-preview
// param ?leagueTabs=1 (the Phase-1 ?leagueRealData=1 idiom; layer &leagueRealData=1
// to preview the tabs over the real adapter). Flag OFF + no param → the byte-
// identical single-column lobby. Do NOT flip the flag here (the PR #510 lesson).
const TABS_ENABLED = LEAGUE_NEXT_ARC_ENABLED || SP.get('leagueTabs') === '1';

export default function LeagueHome({ onOpenMyGame, onOpenTrainingPod, hasAgent, agentLoadout }) {
  const { state: st, isFixtures } = useLeagueState(DEV_FILL);
  const { user } = useUser();
  const uid = user?.uid;

  const aLivePod = React.useMemo(
    () => [...st.rounds.r1, ...st.rounds.r2, st.rounds.r3].find((p) => p.status === 'live') || st.rounds.r1[0],
    [st],
  );

  const [screen, setScreen] = React.useState(DEV_S.startsWith('spectate') ? 'spectate' : 'lobby');
  const [spec, setSpec] = React.useState(null);          // { pod, focusId }
  const [podSheet, setPodSheet] = React.useState(DEV_S === 'pod' ? st.rounds.r1[0] : null);
  // Persistent lobby tab — orthogonal to `screen`; only rendered (via LobbyTabbed)
  // when TABS_ENABLED and screen === 'lobby'. Preserved across the spectate
  // round-trip (spectate is a `screen` change, not an unmount).
  const [tab, setTab] = React.useState('ranked'); // 'ranked' | 'training'

  // Active training pod (Slice 5b-i, D) — the Training-tab re-entry data. A
  // dedicated member-scoped subscription (NOT the fixtures-gated useLeagueState),
  // selected by the shared selectMyTrainingPod predicate: null → the Training tab
  // shows the start CTA, non-null → it shows the re-entry bar instead (R1). Gated
  // on TABS_ENABLED so the flag-off lobby opens no extra listener.
  const [activeTrainingPod, setActiveTrainingPod] = React.useState(null);
  React.useEffect(() => {
    if (!TABS_ENABLED || !uid) { setActiveTrainingPod(null); return undefined; }
    return subscribeMyTrainingPod(uid, setActiveTrainingPod);
  }, [uid]);

  // The per-user active-game signal (Entry-Flow Consolidation Phase 1) — the
  // caller's competitive group via subscribeMyGroup, which observes a claimed
  // slot seat through FORMING/DRAFTING/AWAITING_OPEN/BATTLE. Drives BOTH the
  // conditional MyGameBar (no game → no bar) and the no-game slot-picker center.
  // Fixture mode has no myGroup, so the bar simply hides there.
  const [activeGroup, setActiveGroup] = React.useState(null);
  // Whose seat the subscription has ANSWERED for (it always answers: the
  // group, or null). The Backing strip rides the centre's own slot — the slot
  // picker's or the waiting room's (N3) — so it mounts once that centre is
  // known: a seated viewer's strip mounts ONCE, in its place, rather than
  // under the slot picker first and again under the waiting room (two
  // pod-list requests, two sets of stake listeners). The desktop lobby's rule
  // (WIRE-7, the desktop review record), mirrored.
  const [seatFor, setSeatFor] = React.useState(null);
  React.useEffect(() => {
    if (!uid) { setActiveGroup(null); return undefined; }
    return subscribeMyGroup(uid, (group) => { setActiveGroup(group); setSeatFor(uid); });
  }, [uid]);

  // L-A follow-up (B) — the member voided-card on the DEFAULT League landing (this
  // redesigned lobby is where a member lands, LEAGUE_REDESIGN_ENABLED being on).
  // A dedicated most-recent-voided read, SEPARATE from subscribeMyGroup so the
  // active allowlist stays inert to VOIDED. Surfaced only when there is no active
  // group (auto-expiry: clears when the member's next group forms) and we're on
  // the member's own lobby (not while spectating a rival pod).
  const [voidedGroup, setVoidedGroup] = React.useState(null);
  React.useEffect(() => {
    if (!uid) { setVoidedGroup(null); return undefined; }
    return subscribeMyMostRecentVoidedGroup(uid, setVoidedGroup);
  }, [uid]);

  // seed a dev spectate target once (smoke testing only)
  React.useEffect(() => {
    if (DEV_S === 'spectate-live') {
      const pod = st.rounds.r1.find((p) => p.status === 'live') || aLivePod;
      setSpec({ pod, focusId: pod.seats.find((s) => s && s.you)?.id || pod.seats.find((s) => s)?.id });
    } else if (DEV_S === 'spectate-final') {
      const pod = { ...st.rounds.r1[0], status: 'final' };
      setSpec({ pod, focusId: pod.seats.find((s) => s)?.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signal = (event, payload) => logLeagueSignal(event, payload, { isFixtures });

  const openSpectate = (pod, focusId) => { setPodSheet(null); setSpec({ pod, focusId }); setScreen('spectate'); signal('spectate-open', { podId: pod.id, focusId }); };
  const openPod = (pod) => { setPodSheet(pod); signal('pod-tap', { podId: pod.id }); };
  const backToLobby = () => { setScreen('lobby'); setSpec(null); };
  // Backing: the strip opens the screen; the card's tape link opens the
  // existing spectator view on the COMPLETED week's real pod (group doc +
  // battles through the WHY-projecting endpoint — backingService.fetchTapePod).
  const openBacking = () => { setPodSheet(null); setSpec(null); setScreen('backing'); };
  const openTape = async (groupId, focusId) => {
    try {
      const pod = await fetchTapePod(groupId, uid);
      if (pod) openSpectate(pod, focusId);
    } catch (err) {
      console.warn('[LeagueHome] tape unavailable:', err?.message);
    }
  };
  // The strip's mount — the centre's own slot, under the entry (N3), once the
  // seat is known (above). A component that renders null while dark: no
  // element, no gap. The centre is a gapped column, as on desktop, so the
  // strip brings no margin of its own there (`inColumn` — PLACE-B1).
  const seatKnown = !uid || seatFor === uid;
  const backingSlot = seatKnown ? <BackingLandingStrip uid={uid} accent={ACCENT} onOpen={openBacking} inColumn /> : null;
  // tab-switch: front-end navigation telemetry (NOT a §4 trading-signal).
  // Emitted only on a real switch, never on mount.
  const switchTab = (next) => { if (next === tab) return; signal('tab-switch', { from: tab, to: next }); setTab(next); };

  // Flag-on → the persistent Training|Ranked tabs; flag-off → today's lobby,
  // byte-identical (same <Lobby> invocation, untouched).
  const lobby = TABS_ENABLED
    ? <LobbyTabbed st={st} accent={ACCENT} tab={tab} onSwitchTab={switchTab} onPickPod={openPod} onSpectate={openSpectate} onOpenMyGame={onOpenMyGame} activeGroup={activeGroup} onOpenTrainingPod={onOpenTrainingPod} activeTrainingPod={activeTrainingPod} hasAgent={hasAgent} agentLoadout={agentLoadout} uid={uid} displayName={user?.displayName} backingSlot={backingSlot} />
    : <Lobby st={st} accent={ACCENT} onPickPod={openPod} onSpectate={openSpectate} onOpenMyGame={onOpenMyGame} activeGroup={activeGroup} uid={uid} displayName={user?.displayName} onOpenTrainingPod={onOpenTrainingPod} activeTrainingPod={activeTrainingPod} hasAgent={hasAgent} backingSlot={backingSlot} />;

  // Spectate's claim CTA re-points at the entry (P3): it returns to the lobby,
  // whose center IS the slot picker for a no-game viewer (the retired
  // Pick-your-mode modal is gone).
  const body = screen === 'backing'
    ? <BackingScreen uid={uid} accent={ACCENT} viewport="mobile" onBack={backToLobby} onOpenTape={openTape} />
    : screen === 'spectate' && spec
      ? <Spectate pod={spec.pod} focusId={spec.focusId} accent={ACCENT} onBack={backToLobby} onEnter={backToLobby} />
      : lobby;

  return (
    <div style={{ position: 'relative', minHeight: '100vh', maxWidth: 448, margin: '0 auto', background: LTOKENS.bg, color: LTOKENS.ink }}>
      {!activeGroup && voidedGroup && screen === 'lobby' && (
        <div style={{ padding: '14px 16px 0' }}>
          <LeagueVoidedNotice group={voidedGroup} />
        </div>
      )}
      {body}
      {podSheet && screen === 'lobby' && (
        <PodSheet pod={podSheet} accent={ACCENT} onClose={() => setPodSheet(null)} onSpectate={(seat) => openSpectate(podSheet, seat.id)} />
      )}
    </div>
  );
}
